// THANH-OS boot loader splash shown before TmuxLayout mounts.
//
// Plays a 2-second sequence of "init / agent / gemini / tmux" lines that
// makes the site feel like a multi-agent terminal session attaching, then
// hands off to the 3-pane layout.
//
// Behavior:
//   - Sequential line reveal at ~150ms per line.
//   - DURING animation: any keypress / click fast-forwards to the "ready"
//     state (but does NOT auto-enter the session).
//   - AT ready state: WAITS for the user to actually press a key — never
//     auto-dismisses. This is the moment of intentional entry into the
//     terminal session, not a passive timeout.
//   - `prefers-reduced-motion: reduce` shows everything instantly but still
//     waits for the keypress to enter.

import React, { useEffect, useRef, useState } from 'react';
import { resumeData } from '../../data/resumeData';

interface BootSplashProps {
  onComplete: () => void;
}

interface BootLine {
  tag: string;
  message: string;
  result: string;
  /** Color category for the result token. */
  tone?: 'ok' | 'warn' | 'info';
}

function buildBootLines(): BootLine[] {
  const expCount = resumeData.experience.length;
  const skillCount = resumeData.skills.length;
  const certCount = resumeData.certifications.length;
  // Filesystem node count is roughly: README + (about/2 + experience/N + skills/N + education/M + certs.md + resume.pdf)
  const fsNodes =
    1 + // README.md
    2 + // about/bio.md + about/contact.md
    expCount +
    skillCount +
    resumeData.education.length +
    1 + // certifications.md
    1;  // resume.pdf

  return [
    { tag: 'init',   message: 'Mounting virtual filesystem            ', result: `✓ ${fsNodes} nodes` },
    { tag: 'init',   message: 'Indexing career timeline 2014 → 2026   ', result: `✓ ${expCount} roles` },
    { tag: 'init',   message: 'Loading skill matrix                    ', result: `✓ ${skillCount} domains` },
    { tag: 'init',   message: `Validating ${certCount} certifications         `.slice(0, 40), result: '✓' },
    { tag: 'agent',  message: 'Spawning Career Coach                   ', result: '✓ green' },
    { tag: 'agent',  message: 'Spawning Tech Inspector                 ', result: '✓ amber' },
    { tag: 'gemini', message: 'Verifying VITE_GEMINI_API_KEY           ', result: '✓ active', tone: 'info' },
    { tag: 'tmux',   message: 'Attaching session [0] [1] [2]           ', result: '✓ ok' },
  ];
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const LINE_DELAY_MS = 150;

const BootSplash: React.FC<BootSplashProps> = ({ onComplete }) => {
  const lines = useRef<BootLine[]>(buildBootLines());
  const [revealed, setRevealed] = useState(0);
  const [done, setDone] = useState(false);
  const doneRef = useRef(false);
  const completedRef = useRef(false);

  // Stable completion handler — only fires once even if user mashes keys.
  function complete() {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }

  // Jump straight to the done state without entering the session.
  function fastForward() {
    if (doneRef.current) return;
    doneRef.current = true;
    setRevealed(lines.current.length);
    setDone(true);
  }

  useEffect(() => {
    const reduced = prefersReducedMotion();

    if (reduced) {
      fastForward();
    } else {
      let cancelled = false;
      let i = 0;
      function tick() {
        if (cancelled || doneRef.current) return;
        i += 1;
        setRevealed(i);
        if (i < lines.current.length) {
          setTimeout(tick, LINE_DELAY_MS);
        } else {
          fastForward();
        }
      }
      const start = setTimeout(tick, LINE_DELAY_MS);
      return () => {
        cancelled = true;
        clearTimeout(start);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Single key/pointer listener that switches behavior based on phase:
  //   - animation in progress -> fast-forward to ready state
  //   - already at ready state -> enter the session
  // We bind once for the component's lifetime so the listener can see the
  // updated doneRef without re-binding on every state change.
  useEffect(() => {
    function onPress() {
      if (doneRef.current) {
        complete();
      } else {
        fastForward();
      }
    }
    window.addEventListener('keydown', onPress);
    window.addEventListener('pointerdown', onPress);
    return () => {
      window.removeEventListener('keydown', onPress);
      window.removeEventListener('pointerdown', onPress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleLines = lines.current.slice(0, revealed);

  return (
    <div
      className="fixed inset-0 bg-surface-dark text-primary-400 flex items-center justify-center"
      style={{
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: '13px',
      }}
      aria-label="Boot sequence"
    >
      <div className="w-full max-w-2xl px-6">
        <div className="text-primary-300 mb-1">
          THANH-OS v2.0 &middot; gemini-2.5-flash &middot; pid 1
        </div>
        <div className="text-gray-500 mb-3">
          boot &middot; {new Date().toISOString().replace('T', ' ').slice(0, 19)}
        </div>
        <div className="text-gray-700 mb-4">{'─'.repeat(56)}</div>

        <div className="space-y-1 min-h-[200px]">
          {visibleLines.map((line, idx) => (
            <div key={idx} className="flex items-baseline gap-3">
              <span
                className={
                  line.tone === 'info'
                    ? 'text-secondary-300'
                    : 'text-gray-500'
                }
              >
                [{line.tag.padEnd(6)}]
              </span>
              <span className="text-gray-300 flex-1 whitespace-pre">
                {line.message}
              </span>
              <span className="text-primary-400">{line.result}</span>
            </div>
          ))}
        </div>

        {done && (
          <>
            <div className="text-gray-700 mt-4">{'─'.repeat(56)}</div>
            <div className="text-primary-300 text-center mt-3 animate-pulse">
              ALL SYSTEMS READY
            </div>
            <div className="text-gray-500 text-center mt-1 text-[11px]">
              press any key to enter
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BootSplash;
